import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/users/profileService';
import { UserProfile } from '../../types';
import { getAdminMfaPhase } from '../../config/mfaPolicy';
import {
  getAuthenticatorAssuranceLevel,
  isPrivilegedAdminRole,
  listVerifiedTotpFactors,
  needsMfaChallenge,
  needsMfaEnrollment,
} from '../../services/auth/mfaService';

interface AdminRouteProps {
  children: React.ReactNode;
}

// Admin routes only ever wrap /admin/*, never /mfa/*, so it is always safe to
// sign out on a hard MFA cutover here without trapping a user mid-setup.
type MfaGate = 'allow' | 'enroll' | 'challenge';

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading, signOut } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [mfaGate, setMfaGate] = useState<MfaGate | null>(null);
  const [mfaGateLoading, setMfaGateLoading] = useState(true);
  const [cutoverSignedOut, setCutoverSignedOut] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (user?.id) {
        try {
          const userProfile = await profileService.getProfile(user.id);
          setProfile(userProfile);
        } catch (error) {
          console.error('Failed to load profile:', error);
          setProfile(null);
        }
        setProfileLoading(false);
      } else {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const hasAdminAccess = isPrivilegedAdminRole(profile?.user_role);

  useEffect(() => {
    if (profileLoading) {
      return;
    }

    if (!hasAdminAccess) {
      setMfaGateLoading(false);
      return;
    }

    let cancelled = false;

    const evaluateMfaGate = async () => {
      const phase = getAdminMfaPhase();
      if (phase === 'off') {
        if (!cancelled) {
          setMfaGate('allow');
          setMfaGateLoading(false);
        }
        return;
      }

      try {
        const [factors, aal] = await Promise.all([
          listVerifiedTotpFactors(),
          getAuthenticatorAssuranceLevel(),
        ]);

        if (cancelled) return;

        if (needsMfaEnrollment(profile?.user_role, phase, factors)) {
          setMfaGate('enroll');
        } else if (needsMfaChallenge(profile?.user_role, phase, aal, factors)) {
          setMfaGate('challenge');
        } else {
          setMfaGate('allow');
        }
      } catch (error) {
        console.error('Failed to evaluate admin MFA status:', error);
        // Fail closed: require a fresh challenge rather than silently granting admin access.
        if (!cancelled) {
          setMfaGate('challenge');
        }
      }
      if (!cancelled) {
        setMfaGateLoading(false);
      }
    };

    evaluateMfaGate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, hasAdminAccess, profile?.user_role]);

  useEffect(() => {
    if (mfaGate !== 'enroll') {
      return;
    }

    let cancelled = false;

    const runCutover = async () => {
      // Hard cutover: clear the session before navigating to login so LoginPage's
      // "already authenticated" redirect can't race us back to /welcome with a
      // still-live session.
      try {
        await signOut();
      } finally {
        if (!cancelled) {
          setCutoverSignedOut(true);
        }
      }
    };

    runCutover();

    return () => {
      cancelled = true;
    };
  }, [mfaGate, signOut]);

  const cutoverPending = mfaGate === 'enroll' && !cutoverSignedOut;

  if (authLoading || profileLoading || (hasAdminAccess && mfaGateLoading) || cutoverPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // If not authenticated, send to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Block non-admin users (and failed/missing profile) from admin routes
  if (!profile || !hasAdminAccess) {
    // Redirect to home, which will route them to their appropriate dashboard
    return <Navigate to="/home" replace />;
  }

  if (mfaGate === 'enroll') {
    return <Navigate to="/login?mfa=required" replace />;
  }

  if (mfaGate === 'challenge') {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/mfa/verify?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
